import React from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { BookOpen, Home, BookMarked, FileText, Users, Music, LogOut, Type, Footprints, User, Key, Eye, EyeOff, Clock, Menu } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [siteTexts, setSiteTexts] = React.useState({});
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [passwordForm, setPasswordForm] = React.useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [passwordChanging, setPasswordChanging] = React.useState(false);
  const [newPasswordAlert, setNewPasswordAlert] = React.useState(null);

  const loadSiteTexts = React.useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-texts`);
      const texts = await response.json();
      const textsMap = {};
      texts.forEach(text => {
        textsMap[text.key] = text.value;
      });
      setSiteTexts(textsMap);
    } catch (error) {
      console.error('Error loading site texts:', error);
    }
  }, []);

  React.useEffect(() => {
    loadSiteTexts();
  }, [loadSiteTexts]);

  const getText = (key, defaultValue = '') => {
    return siteTexts[key] || defaultValue;
  };

  // Define menu items structure  
  const menuItemsConfig = [
    { key: 'admin.dashboard', default: 'Dashboard', path: '/admin/dashboard', icon: Home },
    { key: 'admin.surahs', default: 'Sureler', path: '/admin/surahs', icon: BookMarked },
    { key: 'admin.meals', default: 'Mealler', path: '/admin/meals', icon: FileText },
    { key: 'admin.reciters', default: 'Hafızlar', path: '/admin/reciters', icon: Users },
    { key: 'admin.recitations', default: 'Tilavetler', path: '/admin/recitations', icon: Music },
    { key: 'admin.prayer_times', default: 'Namaz Vakitleri', path: '/admin/prayer-times', icon: Clock },
    { key: 'admin.menu_management', default: 'Menü Yönetimi', path: '/admin/menu-management', icon: Menu },
    { key: 'admin.footer_settings', default: 'Footer Ayarları', path: '/admin/footer-settings', icon: Footprints },
    { key: 'admin.settings', default: 'Site Ayarları', path: '/admin/settings', icon: Music },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast({
        title: "Hata",
        description: "Lütfen tüm alanları doldurun",
        variant: "destructive"
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: "Hata",
        description: "Yeni şifreler eşleşmiyor",
        variant: "destructive"
      });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({
        title: "Hata",
        description: "Yeni şifre en az 6 karakter olmalıdır",
        variant: "destructive"
      });
      return;
    }

    setPasswordChanging(true);
    try {
      const formData = new FormData();
      formData.append('current_password', passwordForm.currentPassword);
      formData.append('new_password', passwordForm.newPassword);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setNewPasswordAlert(data.new_password);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        toast({
          title: "Başarılı",
          description: "Şifreniz başarıyla değiştirildi",
          variant: "default"
        });
      } else {
        toast({
          title: "Hata",
          description: data.detail || "Şifre değiştirilemedi",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast({
        title: "Hata",
        description: "Şifre değiştirme sırasında bir hata oluştu",
        variant: "destructive"
      });
    } finally {
      setPasswordChanging(false);
    }
  };

  return (
    <div className="flex h-screen bg-[#F9F6F1]">
      {/* Sidebar */}
      <div className="w-64 bg-[#5C4033] text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-[#6B5744]">
          <div className="flex items-center space-x-2">
            <BookOpen className="h-8 w-8 text-[#D5C4A1]" />
            <div>
              <div className="text-lg font-bold">Kuran-i Kerim</div>
              <div className="text-xs text-[#D5C4A1]">Admin Panel</div>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItemsConfig.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const itemName = getText(item.key, item.default);
            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-[#8B7355] text-white'
                      : 'text-[#D5C4A1] hover:bg-[#6B5744]'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{itemName}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-[#6B5744]">
          <div className="mb-3 px-4">
            <p className="text-sm text-[#D5C4A1]">{getText('admin.login_user', 'Giriş yapan:')}</p>
            <p className="text-white font-medium">{user?.username}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#D5C4A1] hover:bg-[#6B5744] hover:text-white"
          >
            <LogOut className="h-5 w-5 mr-3" />
            {getText('admin.logout', 'Çıkış Yap')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-[#E8DCC8] px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-[#5C4033]">
              {menuItemsConfig.find(item => item.path === location.pathname) 
                ? getText(menuItemsConfig.find(item => item.path === location.pathname)?.key, menuItemsConfig.find(item => item.path === location.pathname)?.default)
                : 'Admin Panel'}
            </h1>
            <div className="flex items-center gap-6">
              <div className="text-sm text-[#8B7355]">
                {new Date().toLocaleDateString('tr-TR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              {/* Profile Icon - Password Change */}
              <button
                onClick={() => setShowPasswordModal(true)}
                className="flex items-center gap-2 text-[#5C4033] hover:text-[#8B7355] transition-colors"
                title="Şifre Değiştir"
              >
                <div className="w-8 h-8 rounded-full bg-[#8B7355] flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-medium">{user?.username || 'Admin'}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-8">
          <Outlet />
        </main>
      </div>

      {/* Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#5C4033]">Şifre Değiştir</DialogTitle>
            <DialogDescription>
              Güvenliğiniz için güçlü bir şifre seçin
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Mevcut Şifre
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="border-[#E8DCC8] focus:border-[#8B7355] pr-10"
                  placeholder="Mevcut şifrenizi girin"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#5C4033]"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Yeni Şifre
              </label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="border-[#E8DCC8] focus:border-[#8B7355] pr-10"
                  placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#5C4033]"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Yeni Şifre (Tekrar)
              </label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Yeni şifrenizi tekrar girin"
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                variant="outline"
                className="flex-1 border-[#E8DCC8] text-[#8B7355] hover:bg-[#F9F6F1]"
                disabled={passwordChanging}
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#8B7355] hover:bg-[#6B5744]"
                disabled={passwordChanging}
              >
                {passwordChanging ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Password Alert Dialog */}
      <Dialog open={!!newPasswordAlert} onOpenChange={() => setNewPasswordAlert(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-600 flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Key className="h-5 w-5 text-green-600" />
              </div>
              Şifre Başarıyla Değiştirildi
            </DialogTitle>
          </DialogHeader>
          
          <Alert className="border-green-200 bg-green-50">
            <AlertDescription className="space-y-3">
              <p className="text-[#5C4033] font-medium">
                Yeni şifreniz:
              </p>
              <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                <p className="text-xl font-mono font-bold text-green-700 text-center select-all">
                  {newPasswordAlert}
                </p>
              </div>
              <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
                <p className="text-sm text-orange-800 font-medium">
                  ⚠️ Lütfen bu şifreyi güvenli bir yere not alın!
                </p>
                <p className="text-xs text-orange-700 mt-1">
                  Bu mesajı kapattıktan sonra yeni şifrenizi göremezsiniz.
                </p>
              </div>
            </AlertDescription>
          </Alert>

          <div className="flex justify-end pt-2">
            <Button
              onClick={() => {
                setNewPasswordAlert(null);
                setShowPasswordModal(false);
              }}
              className="bg-[#8B7355] hover:bg-[#6B5744]"
            >
              Anladım, Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayout;